<div class="text-fg w-full max-w-none">
  <!-- Header -->
  <header class="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
    <div>
      <button type="button" class="cursor-pointer border-0 bg-transparent p-0 text-xs text-muted hover:text-fg" (click)="goBack()">&larr; Back</button>
      @if (account; as acc) {
        <h1 class="mb-1.5 mt-1 text-[1.4rem] font-extrabold tracking-tight text-fg">{{ acc['code'] }} <span class="text-base font-normal text-muted">{{ acc['name'] }}</span></h1>
        <div class="flex flex-wrap gap-1.5">
          <span class="rounded bg-info-bg px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-info-fg">{{ acc['account_type_display'] || acc['account_type'] }}</span>
          <span class="rounded bg-warning-bg px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-warning-fg">{{ acc['currency_code'] }}</span>
          @if (acc['is_system']) { <span class="rounded bg-surface-muted px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">System</span> }
          @if (acc['is_active'] === false) { <span class="rounded bg-danger-bg px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-danger-fg">Inactive</span> }
        </div>
      } @else {
        <h1 class="mb-1.5 mt-1 text-[1.4rem] font-extrabold tracking-tight text-fg">Account Detail</h1>
      }
    </div>
    <button type="button" uiButton variant="outline" size="sm" (click)="loadHub()">
      <ng-icon name="lucideRefreshCw" size="14" />
      <span>Refresh</span>
    </button>
  </header>

  @if (loading()) {
    <div class="p-8 text-center text-sm text-muted">Loading account data...</div>
  } @else if (error(); as err) {
    <div class="rounded-lg bg-danger-bg p-8 text-center text-sm text-danger-fg">{{ err }}</div>
  } @else if (data(); as hub) {
    <!-- KPI Cards -->
    <section class="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-info-bg text-info-fg"><ng-icon name="lucideBanknote" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Balance</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ hub.account['currency_code'] }} {{ formatNumber(hub.balance) }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-danger-bg text-danger-fg"><ng-icon name="lucideTrendingDown" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Total Debits</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ formatNumber(hub.summary.total_debits) }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-success-bg text-success-fg"><ng-icon name="lucideTrendingUp" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Total Credits</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ formatNumber(hub.summary.total_credits) }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideClipboardList" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Journal Entries</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ hub.summary.journal_entry_count }}</span>
          <span class="text-[0.65rem] text-muted">{{ hub.summary.ledger_line_count }} lines</span>
        </div>
      </div>
    </section>

    <!-- Entries by Type -->
    @if (hub.summary.entries_by_type.length > 0) {
      <section class="mb-4 rounded-[0.625rem] border border-border bg-card p-5">
        <h3 class="mb-3.5 border-b border-border pb-2 text-xs font-bold uppercase tracking-wide text-muted">Entries by Type</h3>
        <div class="flex flex-wrap gap-2">
          @for (item of hub.summary.entries_by_type; track item.event_type) {
            <span class="rounded px-2.5 py-1 text-xs font-semibold" [class]="eventTypeClass(item.event_type)">
              {{ item.event_type }} <strong class="ml-1">{{ item.count }}</strong>
            </span>
          }
        </div>
      </section>
    }

    <!-- Account Info -->
    <section class="mb-4 rounded-[0.625rem] border border-border bg-card p-5">
      <h3 class="mb-3.5 border-b border-border pb-2 text-xs font-bold uppercase tracking-wide text-muted">Account Information</h3>
      <div class="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        <div class="flex flex-col"><span class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Code</span><span class="font-mono text-sm text-fg">{{ hub.account['code'] }}</span></div>
        <div class="flex flex-col"><span class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Name</span><span class="text-sm text-fg">{{ hub.account['name'] }}</span></div>
        <div class="flex flex-col"><span class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Type</span><span class="text-sm text-fg">{{ hub.account['account_type_display'] || hub.account['account_type'] }}</span></div>
        <div class="flex flex-col"><span class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Currency</span><span class="text-sm text-fg">{{ hub.account['currency_code'] }} ({{ hub.account['currency_name'] }})</span></div>
        @if (hub.account['description']) {
          <div class="flex flex-col md:col-span-2"><span class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Description</span><span class="text-sm text-fg">{{ hub.account['description'] }}</span></div>
        }
      </div>
    </section>

    <!-- Recent Journal History -->
    <section class="mb-4 rounded-[0.625rem] border border-border bg-card p-5">
      <h3 class="mb-3.5 border-b border-border pb-2 text-xs font-bold uppercase tracking-wide text-muted">Recent Journal History</h3>
      @if (hub.recent_lines.length === 0) {
        <p class="p-8 text-center text-sm text-muted">No ledger entries yet.</p>
      } @else {
        <div uiTableWrap>
          <table uiTable>
            <thead>
              <tr>
                <th>Date</th>
                <th>Journal Ref</th>
                <th>Event</th>
                <th>Side</th>
                <th class="text-right">Amount</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              @for (line of hub.recent_lines; track line.id) {
                <tr>
                  <td class="whitespace-nowrap text-[0.7rem] text-muted">{{ formatDate(line.created_at) }}</td>
                  <td>
                    <a [routerLink]="shellRoutes.commands('manage', 'journal-entries', line.journal_entry_id)" class="font-mono text-xs text-primary hover:underline">
                      {{ line.journal_ref }}
                    </a>
                  </td>
                  <td><span class="rounded px-1.5 py-0.5 text-[0.62rem] font-semibold" [class]="eventTypeClass(line.journal_event_type)">{{ line.journal_event_type }}</span></td>
                  <td>
                    <span class="rounded-sm px-1.5 py-0.5 text-[0.65rem] font-bold" [class]="sideClass(line.side)">
                      {{ line.side_display }}
                    </span>
                  </td>
                  <td class="text-right font-mono">{{ line.currency_code }} {{ formatNumber(line.amount) }}</td>
                  <td class="max-w-[200px] truncate text-xs text-fg-secondary" title="{{ line.description || line.journal_description }}">{{ line.description || line.journal_description }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>
  }
</div>
